import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { X, User, Clock, FileText, AlertTriangle, ThumbsUp, ThumbsDown, Activity, RefreshCw } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { MODAL_OVERLAY, SCALE_IN_Y } from "../lib/animations";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
  Reviewed: "bg-emerald-500/20 text-emerald-400",
  Invalid: "bg-red-500/20 text-red-400",
  PASSED: "bg-emerald-500/20 text-emerald-400",
  FAILED: "bg-red-500/20 text-red-400",
  EXEMPT: "bg-blue-500/20 text-blue-400",
};
const getStatusStyle = (status) => STATUS_STYLES[status] || "bg-amber-500/20 text-amber-400";
const getQuotaStatusStyle = (status) => STATUS_STYLES[status] || "bg-gray-500/20 text-zinc-400";


const StaffProfile = ({ staffId, staffName, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/staff/${staffId}/profile`);
      setData(res.data);
    } catch {
      toast.error("Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const formatTime = (ts) => {
    if (!ts) return "-";
    return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: User },
    { id: "feedback", label: "Feedback", icon: FileText },
    { id: "quota", label: "Quota History", icon: Clock },
    { id: "activity", label: "Activity Log", icon: Activity },
  ];

  return (
    <motion.div
      {...MODAL_OVERLAY}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        {...SCALE_IN_Y}
        className="glass-card w-full max-w-4xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="staff-profile-modal"
      >
        {/* Header */}
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-none bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
                <User className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold text-white" data-testid="profile-name">{staffName}</h2>
                {data?.staff && <p className="text-zinc-400 text-sm">{data.staff.rank} &bull; {data.staff.username}</p>}
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-none">
              <X className="w-5 h-5 text-zinc-400" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-none text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "bg-cyan-500/20 text-cyan-400 border border-cyan-500/30"
                    : "text-zinc-500 hover:bg-zinc-800 border border-transparent"
                }`}
                data-testid={`profile-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)] scrollbar-thin">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-4" />
              <p className="text-zinc-400">Loading profile...</p>
            </div>
          ) : !data ? (
            <p className="text-zinc-500 text-center py-12">Failed to load profile data</p>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === "overview" && (
                <div className="space-y-6">
                  {/* Stats Row */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="glass-card rounded-none p-4 border border-zinc-800">
                      <p className="text-xs text-zinc-500 mb-1">Total Time</p>
                      <p className="text-xl font-bold font-heading text-white">{data.staff.total_time || "0:00"}</p>
                    </div>
                    <div className="glass-card rounded-none p-4 border border-zinc-800">
                      <p className="text-xs text-zinc-500 mb-1">Game Reports</p>
                      <p className="text-xl font-bold font-heading text-cyan-400">{data.staff.game_reports || 0}</p>
                    </div>
                    <div className="glass-card rounded-none p-4 border border-emerald-500/20">
                      <p className="text-xs text-zinc-500 mb-1">Positive Feedback</p>
                      <p className="text-xl font-bold font-heading text-emerald-400">{data.feedback_stats.positive}</p>
                    </div>
                    <div className="glass-card rounded-none p-4 border border-red-500/20">
                      <p className="text-xs text-zinc-500 mb-1">Negative Reports</p>
                      <p className="text-xl font-bold font-heading text-red-400">{data.feedback_stats.negative}</p>
                    </div>
                  </div>

                  {/* Staff Details */}
                  <div className="glass-card rounded-none p-4 border border-zinc-800">
                    <h3 className="font-heading font-bold text-white mb-3">Staff Details</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-zinc-500">Activity:</span> <span className="text-white ml-2">{data.staff.general_activity}</span></div>
                      <div><span className="text-zinc-500">LOA Status:</span> <span className="text-white ml-2">{data.staff.loa_status || "None"}</span></div>
                      <div><span className="text-zinc-500">Strike:</span> <span className="text-white ml-2">{data.staff.strike_status || "None"}</span></div>
                      <div><span className="text-zinc-500">Warning:</span> <span className="text-white ml-2">{data.staff.warning_status || "None"}</span></div>
                      <div><span className="text-zinc-500">Discord ID:</span> <span className="text-white ml-2 font-mono">{data.staff.user_id || "Not set"}</span></div>
                      <div><span className="text-zinc-500">Suspended:</span> <span className="text-white ml-2">{data.staff.suspended || "No"}</span></div>
                    </div>
                    {data.staff.notes && (
                      <div className="mt-3 pt-3 border-t border-zinc-800">
                        <span className="text-zinc-500 text-sm">Notes:</span>
                        <p className="text-zinc-300 text-sm mt-1">{data.staff.notes}</p>
                      </div>
                    )}
                  </div>

                  {data.feedback_stats.avg_severity > 0 && (
                    <div className="glass-card rounded-none p-4 border border-amber-500/20">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-400" />
                        <p className="text-amber-400 font-medium">Average Report Severity: {data.feedback_stats.avg_severity}/5</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Feedback Tab */}
              {activeTab === "feedback" && (
                <div className="space-y-3">
                  {data.feedback.length === 0 ? (
                    <p className="text-zinc-500 text-center py-8">No feedback reports for this staff member</p>
                  ) : (
                    data.feedback.map((f, i) => (
                      <div
                        key={f.id || `feedback-${i}`}
                        className={`glass-card rounded-none p-4 border ${
                          f.feedback_type === "Positive Staff Feedback"
                            ? "border-emerald-500/20"
                            : "border-zinc-800"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {f.feedback_type === "Positive Staff Feedback" ? (
                              <ThumbsUp className="w-4 h-4 text-emerald-400" />
                            ) : (
                              <ThumbsDown className="w-4 h-4 text-red-400" />
                            )}
                            <span className={`text-sm font-medium ${
                              f.feedback_type === "Positive Staff Feedback" ? "text-emerald-400" : "text-red-400"
                            }`}>{f.feedback_type}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            {f.report_severity && (
                              <span className="text-xs text-amber-400">Severity: {f.report_severity}/5</span>
                            )}
                            <span className={`px-2 py-1 rounded text-xs font-bold ${getStatusStyle(f.status)}`}>{f.status}</span>
                          </div>
                        </div>
                        <p className="text-zinc-300 text-sm">{f.details}</p>
                        <p className="text-zinc-600 text-xs mt-2">{formatTime(f.created_at)}</p>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Quota History Tab */}
              {activeTab === "quota" && (
                <div>
                  {data.quota_history.length === 0 ? (
                    <p className="text-zinc-500 text-center py-8">No quota history for this staff member</p>
                  ) : (
                    <table className="w-full">
                      <thead>
                        <tr className="table-header">
                          <th className="text-left py-3 px-4">Week</th>
                          <th className="text-left py-3 px-4">Time Logged</th>
                          <th className="text-left py-3 px-4">Game Reports</th>
                          <th className="text-left py-3 px-4">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.quota_history.map((q, i) => (
                          <tr key={q.week_start || `quota-${i}`} className="table-row">
                            <td className="py-3 px-4 text-zinc-400 text-sm">{q.week_start} — {q.week_end}</td>
                            <td className="py-3 px-4 text-white font-mono text-sm">{q.time_logged || "-"}</td>
                            <td className="py-3 px-4 text-cyan-400 text-sm">{q.game_reports || 0}</td>
                            <td className="py-3 px-4">
                              <span className={`px-2 py-1 rounded text-xs font-bold ${getQuotaStatusStyle(q.activity_status)}`}>{q.activity_status || "Pending"}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* Activity Log Tab */}
              {activeTab === "activity" && (
                <div className="space-y-2">
                  {data.activity_logs.length === 0 ? (
                    <p className="text-zinc-500 text-center py-8">No activity logs for this staff member</p>
                  ) : (
                    data.activity_logs.map((log, i) => (
                      <div key={log.id || `log-${i}`} className="glass-card rounded-none p-3 border border-zinc-800 flex items-center justify-between">
                        <div>
                          <span className="text-xs font-mono font-bold text-cyan-400 mr-2">{log.action}</span>
                          <span className="text-sm text-zinc-300">{log.details}</span>
                        </div>
                        <span className="text-xs text-zinc-600 whitespace-nowrap ml-4">{formatTime(log.timestamp)}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default StaffProfile;
